import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ticketService } from '../services';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Ticket, Plus, Clock, CheckCircle, AlertCircle, Cloud, Database, X, ArrowLeft, MessageSquare, User, Mail, Calendar, Building, Loader2, Filter, ChevronDown, Send } from 'lucide-react';
import { toast } from 'sonner';

export const TicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [ticketSource, setTicketSource] = useState(null);
  const [sourceError, setSourceError] = useState(null);
  const [contactFound, setContactFound] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    priority: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Departments for new ticket
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  
  // Ticket detail view
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [ticketThreads, setTicketThreads] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // New ticket form
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    category: ''
  });
  const [creatingTicket, setCreatingTicket] = useState(false);
  
  // New comment
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const data = await ticketService.getTickets(filters);
      if (data.tickets) {
        setTickets(data.tickets);
        setTicketSource(data.source);
        setSourceError(data.error);
        setContactFound(data.contact_found);
      } else if (Array.isArray(data)) {
        setTickets(data);
        setTicketSource('legacy');
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setSourceError('Fehler beim Laden der Tickets');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const data = await ticketService.getDepartments();
      console.log('Departments loaded:', data); // Debug log
      if (data.departments && data.departments.length > 0) {
        setDepartments(data.departments);
        // Set first department as default if category not set
        if (!newTicket.category) {
          setNewTicket(prev => ({ ...prev, category: data.departments[0].id }));
        }
      } else {
        console.error('No departments in response:', data);
        setDepartments([]);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const openTicketDetail = async (ticket) => {
    setSelectedTicket(ticket);
    setLoadingDetail(true);
    
    try {
      const [detail, threads] = await Promise.all([
        ticketService.getTicketDetail(ticket.ticket_id),
        ticketService.getTicketThreads(ticket.ticket_id)
      ]);
      
      setTicketDetail(detail);
      setTicketThreads(threads.threads || []);
    } catch (error) {
      console.error('Error fetching ticket detail:', error);
      toast.error('Fehler beim Laden der Ticket-Details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeTicketDetail = () => {
    setSelectedTicket(null);
    setTicketDetail(null);
    setTicketThreads([]);
    setNewComment('');
  };

  const handleSendComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) {
      toast.error('Bitte geben Sie einen Kommentar ein');
      return;
    }
    
    setSendingComment(true);
    try {
      const result = await ticketService.addTicketComment(
        selectedTicket.ticket_id,
        newComment,
        true // is_public
      );
      
      if (result.success) {
        toast.success('Kommentar erfolgreich hinzugefügt!');
        setNewComment('');
        
        // Refresh threads
        const threads = await ticketService.getTicketThreads(selectedTicket.ticket_id);
        setTicketThreads(threads.threads || []);
      } else {
        toast.error(result.error || 'Fehler beim Senden');
      }
    } catch (error) {
      console.error('Error sending comment:', error);
      toast.error('Fehler beim Senden des Kommentars');
    } finally {
      setSendingComment(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject || !newTicket.description || !newTicket.category) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    setCreatingTicket(true);
    try {
      const result = await ticketService.createTicket(newTicket);
      if (result.success || result.ticket_number) {
        toast.success(`Ticket ${result.ticket_number || ''} erfolgreich in Zoho erstellt!`);
        setIsDialogOpen(false);
        setNewTicket({ subject: '', description: '', priority: 'medium', category: departments[0]?.id || '' });
        fetchTickets();
      } else {
        toast.error(result.error || 'Fehler beim Erstellen');
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Zoho Desk ist nicht erreichbar. Ticket konnte nicht erstellt werden.');
    } finally {
      setCreatingTicket(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    if (isDialogOpen) {
      // Always fetch departments when dialog opens
      fetchDepartments();
    }
  }, [isDialogOpen]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { label: 'Offen', className: 'bg-blue-100 text-blue-800 border-blue-200' },
      pending: { label: 'In Bearbeitung', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
      resolved: { label: 'Gelöst', className: 'bg-green-100 text-green-800 border-green-200' },
      closed: { label: 'Geschlossen', className: 'bg-slate-100 text-slate-800 border-slate-200' }
    };
    const config = statusConfig[status] || statusConfig.open;
    return <Badge className={`border ${config.className}`}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      high: { label: 'Hoch', className: 'bg-red-100 text-red-800 border-red-200' },
      medium: { label: 'Mittel', className: 'bg-orange-100 text-orange-800 border-orange-200' },
      low: { label: 'Niedrig', className: 'bg-slate-100 text-slate-600 border-slate-200' }
    };
    const config = priorityConfig[priority] || priorityConfig.medium;
    return <Badge className={`border ${config.className}`}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Ticket Detail View
  if (selectedTicket) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        data-testid="ticket-detail-view"
      >
        {/* Back Button & Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={closeTicketDetail}
            className="mb-4"
            data-testid="back-to-tickets"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zur Übersicht
          </Button>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : ticketDetail ? (
            <>
              {/* Ticket Header */}
              <Card className="mb-6 bg-white border-slate-200">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-slate-500 font-mono mb-1">{ticketDetail.ticket_number}</p>
                      <CardTitle className="text-2xl" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                        {ticketDetail.subject}
                      </CardTitle>
                    </div>
                    <div className="flex gap-2">
                      {getStatusBadge(ticketDetail.status_mapped)}
                      {getPriorityBadge(ticketDetail.priority_mapped)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Ticket Meta Info */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Calendar className="w-4 h-4" />
                      <span>Erstellt: {ticketDetail.created_at ? new Date(ticketDetail.created_at).toLocaleString('de-DE') : '-'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Building className="w-4 h-4" />
                      <span>Abteilung: {ticketDetail.department || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <User className="w-4 h-4" />
                      <span>Bearbeiter: {ticketDetail.assignee || 'Nicht zugewiesen'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail className="w-4 h-4" />
                      <span>Kanal: {ticketDetail.channel || '-'}</span>
                    </div>
                  </div>
                  
                  {/* Ticket Description */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h3 className="font-medium text-slate-900 mb-2">Beschreibung</h3>
                    <div 
                      className="prose prose-sm max-w-none text-slate-700"
                      dangerouslySetInnerHTML={{ __html: ticketDetail.description || 'Keine Beschreibung' }}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Conversation Thread */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Konversation ({ticketThreads.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Add Comment Form */}
                  <form onSubmit={handleSendComment} className="mb-6">
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <Textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Schreiben Sie eine Antwort oder einen Kommentar..."
                          rows={3}
                          className="resize-none"
                          data-testid="comment-input"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end mt-3">
                      <Button
                        type="submit"
                        disabled={sendingComment || !newComment.trim()}
                        style={{
                          backgroundColor: 'var(--color-primary)',
                          color: 'var(--color-text-on-primary)'
                        }}
                        data-testid="send-comment-button"
                      >
                        {sendingComment ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Sende...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Kommentar senden
                          </>
                        )}
                      </Button>
                    </div>
                  </form>

                  {/* Thread Messages */}
                  {ticketThreads.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Keine Nachrichten vorhanden</p>
                      <p className="text-sm mt-1">Starten Sie die Konversation mit einem Kommentar</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {ticketThreads.map((thread, index) => (
                        <motion.div
                          key={thread.thread_id || index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className={`p-4 rounded-lg ${
                            thread.direction === 'in' 
                              ? 'bg-blue-50 border border-blue-100 ml-0 mr-8' 
                              : 'bg-slate-50 border border-slate-100 ml-8 mr-0'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                thread.direction === 'in' ? 'bg-blue-200 text-blue-800' : 'bg-slate-200 text-slate-800'
                              }`}>
                                {thread.author?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div>
                                <p className="font-medium text-sm text-slate-900">{thread.author || 'Unbekannt'}</p>
                                <p className="text-xs text-slate-500">{thread.author_type === 'AGENT' ? 'Support' : 'Kunde'}</p>
                              </div>
                            </div>
                            <span className="text-xs text-slate-500">
                              {thread.created_at ? new Date(thread.created_at).toLocaleString('de-DE') : ''}
                            </span>
                          </div>
                          <div 
                            className="text-sm text-slate-700 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: thread.content || thread.content_text || '' }}
                          />
                        </motion.div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : null}
        </div>
      </motion.div>
    );
  }

  // Tickets List View
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="tickets-page"
    >
      {/* Page Header */}
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--color-text-primary)' }}>
            Support Tickets
          </h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>Verwalten Sie Ihre Support-Anfragen</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              style={{
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-text-on-primary)'
              }}
              className="hover:opacity-90"
              data-testid="create-ticket-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Neues Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[525px]">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Neues Support-Ticket erstellen</DialogTitle>
              <DialogDescription>
                Beschreiben Sie Ihr Anliegen und wir werden uns schnellstmöglich darum kümmern.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateTicket} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="subject">Betreff *</Label>
                <Input
                  id="subject"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Kurze Beschreibung des Problems"
                  required
                  data-testid="ticket-subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Beschreibung *</Label>
                <Textarea
                  id="description"
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  placeholder="Detaillierte Beschreibung Ihres Anliegens..."
                  rows={4}
                  required
                  data-testid="ticket-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Kategorie *</Label>
                  {loadingDepartments ? (
                    <div className="flex items-center gap-2 h-10 px-3 text-sm text-slate-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Lade Kategorien...
                    </div>
                  ) : departments.length === 0 ? (
                    <div className="flex items-center gap-2 h-10 px-3 text-sm text-red-500">
                      Keine Kategorien verfügbar
                    </div>
                  ) : (
                    <Select
                      value={newTicket.category}
                      onValueChange={(value) => setNewTicket({ ...newTicket, category: value })}
                    >
                      <SelectTrigger 
                        className="w-full bg-white" 
                        data-testid="ticket-category-select"
                      >
                        <SelectValue placeholder="Bitte wählen..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white max-h-60">
                        {departments.map((dept) => (
                          <SelectItem key={dept.id} value={dept.id}>
                            {dept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority">Priorität</Label>
                  <Select
                    value={newTicket.priority}
                    onValueChange={(value) => setNewTicket({ ...newTicket, priority: value })}
                  >
                    <SelectTrigger className="w-full bg-white" data-testid="ticket-priority-select">
                      <SelectValue placeholder="Priorität wählen" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="low">Niedrig</SelectItem>
                      <SelectItem value="medium">Mittel</SelectItem>
                      <SelectItem value="high">Hoch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsDialogOpen(false)}
                >
                  Abbrechen
                </Button>
                <Button 
                  type="submit"
                  disabled={creatingTicket}
                  style={{
                    backgroundColor: 'var(--color-primary)',
                    color: 'var(--color-text-on-primary)'
                  }}
                  data-testid="submit-ticket-button"
                >
                  {creatingTicket ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Erstelle...
                    </>
                  ) : (
                    'Ticket erstellen'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className="mb-3"
          data-testid="toggle-filters"
        >
          <Filter className="w-4 h-4 mr-2" />
          Filter
          <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </Button>
        
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <Card className="bg-white border-slate-200">
                <CardContent className="pt-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <select
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        className="flex h-10 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                        data-testid="filter-status"
                      >
                        <option value="">Alle Status</option>
                        <option value="open">Offen</option>
                        <option value="pending">In Bearbeitung</option>
                        <option value="resolved">Gelöst</option>
                        <option value="closed">Geschlossen</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Priorität</Label>
                      <select
                        value={filters.priority}
                        onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                        className="flex h-10 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                        data-testid="filter-priority"
                      >
                        <option value="">Alle Prioritäten</option>
                        <option value="high">Hoch</option>
                        <option value="medium">Mittel</option>
                        <option value="low">Niedrig</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <Button
                        variant="outline"
                        onClick={() => setFilters({ status: '', priority: '' })}
                        className="w-full"
                        data-testid="clear-filters"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Filter zurücksetzen
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Data Source Info */}
      {ticketSource && (
        <div className={`mb-6 p-4 rounded-lg border flex items-center gap-3 ${
          ticketSource === 'zoho_desk' 
            ? 'bg-green-50 border-green-200' 
            : ticketSource === 'error' || ticketSource === 'no_credentials'
            ? 'bg-yellow-50 border-yellow-200'
            : 'bg-blue-50 border-blue-200'
        }`}>
          {ticketSource === 'zoho_desk' ? (
            <>
              <Cloud className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800">
                  Live-Daten von Zoho Desk
                </p>
                <p className="text-xs text-green-600">
                  {contactFound 
                    ? 'Tickets werden nach Ihrer E-Mail-Adresse gefiltert' 
                    : 'Kein Kontakt mit Ihrer E-Mail gefunden - zeige alle Tickets'
                  }
                </p>
              </div>
            </>
          ) : ticketSource === 'no_credentials' || ticketSource === 'error' ? (
            <>
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  {sourceError || 'Zoho Desk nicht verbunden'}
                </p>
                <p className="text-xs text-yellow-600">
                  Bitte konfigurieren Sie die Zoho Desk Verbindung im Admin-Bereich
                </p>
              </div>
            </>
          ) : (
            <>
              <Database className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-800">
                  Lokale Daten
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Tickets List */}
      <div className="grid gap-4">
        {tickets.length === 0 ? (
          <Card className="bg-white border-slate-200">
            <CardContent className="pt-6 text-center py-12">
              <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">Keine Tickets gefunden</h3>
              <p className="text-sm text-slate-500">
                {ticketSource === 'zoho_desk' 
                  ? 'Es wurden keine Tickets für Ihre E-Mail-Adresse in Zoho Desk gefunden.'
                  : 'Erstellen Sie Ihr erstes Support-Ticket.'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          tickets.map((ticket) => (
            <Card 
              key={ticket.ticket_id} 
              className="bg-white border-slate-200 hover:shadow-md transition-shadow cursor-pointer" 
              data-testid={`ticket-card-${ticket.ticket_number}`}
              onClick={() => openTicketDetail(ticket)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <Ticket className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                          {ticket.subject}
                        </h3>
                        {getStatusBadge(ticket.status)}
                        {getPriorityBadge(ticket.priority)}
                      </div>
                      <p className="text-sm text-slate-600 mb-3 line-clamp-2">{ticket.description || 'Keine Beschreibung'}</p>
                      <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                        <span className="font-mono">{ticket.ticket_number}</span>
                        {ticket.department && (
                          <>
                            <span>•</span>
                            <span>Abteilung: {ticket.department}</span>
                          </>
                        )}
                        {ticket.assignee && (
                          <>
                            <span>•</span>
                            <span>Bearbeiter: {ticket.assignee}</span>
                          </>
                        )}
                        <span>•</span>
                        <span>Erstellt: {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString('de-DE') : '-'}</span>
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    data-testid={`view-ticket-${ticket.ticket_number}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openTicketDetail(ticket);
                    }}
                  >
                    Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </motion.div>
  );
};
